/**
 * Error codes returned by the backend
 */
export const ErrorCodes = {
  INVALID_TICKER: 'INVALID_TICKER',
  INVALID_ASSET_TYPE: 'INVALID_ASSET_TYPE',
  INVALID_OPERATION_TYPE: 'INVALID_OPERATION_TYPE',
  INVALID_CURRENCY: 'INVALID_CURRENCY',
  INVALID_QUANTITY: 'INVALID_QUANTITY',
  INVALID_VALUE: 'INVALID_VALUE',
  INVALID_DATE: 'INVALID_DATE',
  INVALID_FIELD: 'INVALID_FIELD',
  REQUIRED_FIELD: 'REQUIRED_FIELD',
  NOT_FOUND: 'NOT_FOUND',
  DUPLICATE_ENTRY: 'DUPLICATE_ENTRY',
  UNAUTHORIZED: 'UNAUTHORIZED',
  BAD_REQUEST: 'BAD_REQUEST',
  INVALID_JSON: 'INVALID_JSON',

  // Correlation error codes
  CORRELATION_SELF_REFERENCE: 'CORRELATION_SELF_REFERENCE',
  CORRELATION_PARENT_NOT_FOUND: 'CORRELATION_PARENT_NOT_FOUND',
  CORRELATION_PARENT_IS_CHILD: 'CORRELATION_PARENT_IS_CHILD',
  CORRELATION_CHILD_HAS_CHILDREN: 'CORRELATION_CHILD_HAS_CHILDREN',
  CORRELATION_ALREADY_LINKED: 'CORRELATION_ALREADY_LINKED',
  CORRELATION_VALUE_EXCEEDS_PARENT: 'CORRELATION_VALUE_EXCEEDS_PARENT',
  CORRELATION_USER_MISMATCH: 'CORRELATION_USER_MISMATCH',
  CORRELATION_NOT_FOUND: 'CORRELATION_NOT_FOUND',
};

/**
 * User-friendly messages for each error code (Portuguese)
 */
const errorMessages = {
  [ErrorCodes.INVALID_TICKER]: 'Ticker do ativo inválido',
  [ErrorCodes.INVALID_ASSET_TYPE]: 'Classe de ativo inválida',
  [ErrorCodes.INVALID_OPERATION_TYPE]: 'Tipo de operação inválido',
  [ErrorCodes.INVALID_CURRENCY]: 'Moeda inválida',
  [ErrorCodes.INVALID_QUANTITY]: 'Quantidade inválida',
  [ErrorCodes.INVALID_VALUE]: 'Valor inválido',
  [ErrorCodes.INVALID_DATE]: 'Data inválida',
  [ErrorCodes.INVALID_FIELD]: 'Campo inválido',
  [ErrorCodes.REQUIRED_FIELD]: 'Campo obrigatório não preenchido',
  [ErrorCodes.NOT_FOUND]: 'Recurso não encontrado',
  [ErrorCodes.DUPLICATE_ENTRY]: 'Este registro já existe',
  [ErrorCodes.UNAUTHORIZED]: 'Não autorizado. Faça login novamente',
  [ErrorCodes.BAD_REQUEST]: 'Requisição inválida',
  [ErrorCodes.INVALID_JSON]: 'Formato de dados inválido',

  // Correlation error messages
  [ErrorCodes.CORRELATION_SELF_REFERENCE]: 'Não é possível correlacionar uma despesa a ela mesma',
  [ErrorCodes.CORRELATION_PARENT_NOT_FOUND]: 'Despesa principal não encontrada',
  [ErrorCodes.CORRELATION_PARENT_IS_CHILD]: 'Esta despesa já está correlacionada a outra despesa',
  [ErrorCodes.CORRELATION_CHILD_HAS_CHILDREN]: 'Esta despesa possui despesas correlacionadas e não pode ser vinculada',
  [ErrorCodes.CORRELATION_ALREADY_LINKED]: 'Esta despesa já está correlacionada a outra despesa',
  [ErrorCodes.CORRELATION_VALUE_EXCEEDS_PARENT]: 'O valor total correlacionado excede o valor da despesa principal',
  [ErrorCodes.CORRELATION_USER_MISMATCH]: 'As despesas pertencem a usuários diferentes',
  [ErrorCodes.CORRELATION_NOT_FOUND]: 'Correlação não encontrada',
};

/**
 * Field name translations (Portuguese)
 * Set to empty string ('') to hide the field name in error messages
 */
const fieldTranslations = {
  email: 'E-mail',
  password: 'Senha',
  name: 'Nome',
  description: 'Descrição',
  category: 'Categoria',
  categoryId: 'Categoria',
  CategoryId: 'Categoria',
  currency: 'Moeda',
  Currency: 'Moeda',
  amount: 'Valor',
  Amount: 'Valor',
  value: 'Valor',
  Value: 'Valor',
  date: 'Data',
  receivedDate: 'Data de recebimento',
  ReceivedDate: 'Data de recebimento',
  debitDate: 'Data de débito',
  DebitDate: 'Data de débito',
  operationDate: 'Data da operação',
  OperationDate: 'Data da operação',
  asset: 'Ativo',
  Asset: 'Ativo',
  'asset.name': '',
  'asset.Name': '',
  'Asset.Name': '',
  'asset.class': '',
  'asset.Class': '',
  'Asset.Class': '',
  ticker: 'Ticker',
  Ticker: 'Ticker',
  quantity: 'Quantidade',
  Quantity: 'Quantidade',
  unitValue: 'Valor unitário',
  UnitValue: 'Valor unitário',
  fees: 'Taxas',
  Fees: 'Taxas',
  operationType: 'Tipo de operação',
  OperationType: 'Tipo de operação',
  assetClass: 'Classe de ativo',
  Class: 'Classe de ativo',
  body: '',
  request: '',
  credentials: '',
  Credentials: '',

  // Correlation fields
  parent_id: 'Despesa principal',
  parentId: 'Despesa principal',
  child_expense_id: 'Despesa a correlacionar',
  childExpenseId: 'Despesa a correlacionar',
  child_id: 'Despesa correlacionada',
  childId: 'Despesa correlacionada',
};

/**
 * Message translations (English -> Portuguese)
 * Add new translations here as needed
 */
const messageTranslations = {
  // Validation messages
  'is required': 'é obrigatório',
  'is invalid': 'é inválido',
  'must be a valid email': 'deve ser um e-mail válido',
  'must be at least': 'deve ter no mínimo',
  'must be at most': 'deve ter no máximo',
  'characters': 'caracteres',
  'must be greater than': 'deve ser maior que',
  'must be less than': 'deve ser menor que',
  'must be a positive number': 'deve ser um número positivo',
  'must be a number': 'deve ser um número',
  'already exists': 'já existe',
  'not found': 'não encontrado',
  'cannot be empty': 'não pode estar vazio',
  'invalid format': 'formato inválido',
  'invalid date': 'data inválida',
  'invalid value': 'valor inválido',

  // Asset/Operation messages
  'invalid ticker': 'ticker inválido',
  'invalid asset class': 'classe de ativo inválida',
  'invalid operation type': 'tipo de operação inválido',
  'invalid currency': 'moeda inválida',
  'invalid quantity': 'quantidade inválida',
  'asset not found': 'ativo não encontrado',
  'operation not found': 'operação não encontrada',
  'insufficient balance': 'saldo insuficiente',
  'insufficient quantity': 'quantidade insuficiente',

  // Auth messages
  'invalid credentials': 'credenciais inválidas',
  'invalid password': 'senha inválida',
  'invalid email': 'e-mail inválido',
  'user not found': 'usuário não encontrado',
  'user or password invalid': 'usuário ou senha inválidos',
  'email already registered': 'e-mail já cadastrado',
  'token expired': 'sessão expirada',
  'unauthorized': 'não autorizado',

  // Generic messages
  'internal server error': 'erro interno do servidor',
  'bad request': 'requisição inválida',
  'validation error': 'erro de validação',
  'missing required field': 'campo obrigatório não preenchido',
};

/**
 * Translates a message from English to Portuguese
 * Uses partial matching to handle dynamic messages
 */
function translateMessage(message) {
  if (!message) return message;

  const lowerMessage = message.toLowerCase();

  // Check for exact match first
  if (messageTranslations[lowerMessage]) {
    return messageTranslations[lowerMessage];
  }

  // Check for partial matches and replace
  let translatedMessage = message;
  for (const [english, portuguese] of Object.entries(messageTranslations)) {
    const regex = new RegExp(english, 'gi');
    translatedMessage = translatedMessage.replace(regex, portuguese);
  }

  return translatedMessage;
}

/**
 * Represents a parsed API error
 */
export class ApiError {
  constructor(code, message, details = [], statusCode = null) {
    this.code = code;
    this.message = message;
    this.details = details;
    this.statusCode = statusCode;
  }

  hasFieldErrors() {
    return this.details.length > 0;
  }

  getFieldError(fieldName) {
    const detail = this.details.find(
      (d) => d.field.toLowerCase() === fieldName.toLowerCase()
    );
    return detail ? translateFieldError(detail) : null;
  }

  getAllFieldErrors() {
    return this.details.map((detail) => translateFieldError(detail));
  }

  getDisplayMessage() {
    if (this.hasFieldErrors()) {
      const fieldErrors = this.getAllFieldErrors();
      if (fieldErrors.length === 1) {
        return fieldErrors[0];
      }
      return `${translateMessage(this.message)}: ${fieldErrors.join(', ')}`;
    }
    return translateMessage(this.message);
  }
}

/**
 * Translates a field error to a user-friendly message
 */
function translateFieldError(detail) {
  const fieldKey = detail.field?.toLowerCase();
  const fieldName = fieldTranslations[detail.field] ?? fieldTranslations[fieldKey] ?? detail.field;
  const reason = translateMessage(detail.reason);

  // If field name is empty string, just return the reason
  if (fieldName === '') {
    return reason;
  }

  return `${fieldName}: ${reason}`;
}

/**
 * Gets a user-friendly message for an error code
 */
function getMessageForCode(code, defaultMessage) {
  return errorMessages[code] || defaultMessage || 'Ocorreu um erro inesperado';
}

/**
 * Parses an Axios error response into an ApiError
 */
export function parseApiError(error, defaultMessage = 'Ocorreu um erro inesperado') {
  if (!error.response) {
    return new ApiError(
      'NETWORK_ERROR',
      'Erro de conexão. Verifique sua internet e tente novamente.',
      [],
      null
    );
  }

  const { status, data } = error.response;

  if (data && data.error === 'validation_error') {
    return new ApiError(
      data.code,
      getMessageForCode(data.code, data.message),
      data.details || [],
      status
    );
  }

  if (data && data.code) {
    return new ApiError(
      data.code,
      getMessageForCode(data.code, data.message),
      data.details || [],
      status
    );
  }

  if (status === 401) {
    return new ApiError(
      ErrorCodes.UNAUTHORIZED,
      'Sessão expirada. Faça login novamente.',
      [],
      status
    );
  }

  if (status === 404) {
    return new ApiError(
      ErrorCodes.NOT_FOUND,
      'Recurso não encontrado.',
      [],
      status
    );
  }

  if (status >= 500) {
    return new ApiError(
      'SERVER_ERROR',
      'Erro no servidor. Tente novamente mais tarde.',
      [],
      status
    );
  }

  return new ApiError('UNKNOWN_ERROR', defaultMessage, [], status);
}

/**
 * Checks if an error is an ApiError instance
 */
export function isApiError(error) {
  return error instanceof ApiError;
}

/**
 * Extracts error message from any error type
 */
export function getErrorMessage(error, defaultMessage = 'Ocorreu um erro inesperado') {
  if (isApiError(error)) {
    return error.getDisplayMessage();
  }

  if (error.apiError && isApiError(error.apiError)) {
    return error.apiError.getDisplayMessage();
  }

  if (error.originalError) {
    return getErrorMessage(error.originalError, defaultMessage);
  }

  if (error.response) {
    const apiError = parseApiError(error, defaultMessage);
    return apiError.getDisplayMessage();
  }

  if (error.message && !error.message.includes('AbortError')) {
    return translateMessage(error.message);
  }

  return defaultMessage;
}

/**
 * Adds custom translations to the message translations map
 * Useful for adding domain-specific translations at runtime
 */
export function addMessageTranslations(translations) {
  Object.assign(messageTranslations, translations);
}

/**
 * Export translateMessage for external use
 */
export { translateMessage };
